import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/hooks/useAuth';
import { mensagemAmigavel } from '@/lib/mensagemAmigavel';
import FichaImpressao from './components/FichaImpressao';
import { STATUS_LABEL, formatarDataCurta } from '@/pages/gerenciar/utils';
import type { StatusInscricao } from '@/components/feature/SeloSituacao';
import type { PessoaImpressao, RespostaImpressao } from './types';

type Estado = 'carregando' | 'pronto' | 'sem-acesso' | 'erro';

interface LinhaInscrito {
  inscricao_id: number;
  modalidade_id: number;
  modalidade: string;
  grupo: string | null;
  participante: string;
  participante_telefone: string | null;
  participante_email: string | null;
  cpf: string | null;
  nascimento: string | null;
  responsavel: string;
  responsavel_telefone: string | null;
  status: StatusInscricao;
}

interface LinhaResposta {
  inscricao_id: number;
  ordem: number;
  enunciado: string;
  tipo: string | null;
  resposta_texto: string | null;
}

interface LinhaDetalhe {
  id: number;
  senha_fila: string | null;
  senha_num: number | null;
}

interface LinhaFila {
  inscricao_id: number;
  posicao: number;
  senha: string | null;
}

interface GrupoVagas {
  modalidade_id: number;
  vagas: number | null;
}

function normalizar(s: string): string {
  return s
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .trim()
    .toLowerCase();
}

function calcularIdade(nascimento: string | null, referencia: string): number | null {
  if (!nascimento) return null;
  const n = new Date(nascimento);
  const r = new Date(referencia);
  if (Number.isNaN(n.getTime()) || Number.isNaN(r.getTime())) return null;
  let idade = r.getFullYear() - n.getFullYear();
  const m = r.getMonth() - n.getMonth();
  if (m < 0 || (m === 0 && r.getDate() < n.getDate())) idade -= 1;
  return idade;
}

function agoraTexto(): string {
  const d = new Date();
  const dia = String(d.getDate()).padStart(2, '0');
  const mes = String(d.getMonth() + 1).padStart(2, '0');
  const hh = String(d.getHours()).padStart(2, '0');
  const mm = String(d.getMinutes()).padStart(2, '0');
  return `${dia}/${mes}/${d.getFullYear()} ${hh}:${mm}`;
}

export default function FichaImprimirPage() {
  const navigate = useNavigate();
  const { user, pronto: authPronto } = useAuth();
  const { eventoId, inscricaoId } = useParams();
  const [searchParams] = useSearchParams();
  const eventoIdNum = Number(eventoId);

  const [estado, setEstado] = useState<Estado>('carregando');
  const [erro, setErro] = useState<string | null>(null);
  const [eventoNome, setEventoNome] = useState('');
  const [eventoData, setEventoData] = useState('');
  const [pessoas, setPessoas] = useState<PessoaImpressao[]>([]);

  const ids = useMemo(() => {
    const doParametro = searchParams.get('ids');
    if (doParametro) {
      return doParametro
        .split(',')
        .map((v) => Number(v.trim()))
        .filter((v) => Number.isFinite(v) && v > 0);
    }
    const n = Number(inscricaoId);
    return Number.isFinite(n) && n > 0 ? [n] : [];
  }, [searchParams, inscricaoId]);

  useEffect(() => {
    if (!authPronto) return;
    if (!user) {
      navigate(`/entrar?voltar=${encodeURIComponent(window.location.pathname + window.location.search)}`);
      return;
    }
    if (Number.isNaN(eventoIdNum) || ids.length === 0) {
      setEstado('sem-acesso');
      return;
    }
    let ativo = true;
    (async () => {
      setEstado('carregando');
      setErro(null);
      try {
        const { data: eventoData_, error: errEv } = await supabase
          .from('eventos')
          .select('id, nome, inicio_em, cidade, uf, local_nome, dono_id')
          .eq('id', eventoIdNum)
          .maybeSingle();
        if (errEv) throw errEv;
        if (!eventoData_) {
          if (ativo) setEstado('sem-acesso');
          return;
        }
        const evento = eventoData_ as {
          nome: string;
          inicio_em: string;
          dono_id: string;
          cidade: string | null;
          uf: string | null;
          local_nome: string | null;
        };

        let permitido = evento.dono_id === user.id;
        if (!permitido) {
          const { data: equipe } = await supabase
            .from('equipe_evento')
            .select('id')
            .eq('evento_id', eventoIdNum)
            .eq('usuario_id', user.id)
            .eq('status', 'ativo')
            .maybeSingle();
          permitido = !!equipe;
        }
        if (!permitido) {
          if (ativo) setEstado('sem-acesso');
          return;
        }

        const [resInscritos, resDetalhe, resFila, resPainel, resRespostas] = await Promise.all([
          supabase
            .from('vw_inscritos')
            .select(
              'inscricao_id, modalidade_id, modalidade, grupo, participante, participante_telefone, participante_email, cpf, nascimento, responsavel, responsavel_telefone, status',
            )
            .eq('evento_id', eventoIdNum)
            .in('inscricao_id', ids),
          supabase
            .from('inscricoes')
            .select('id, senha_fila, senha_num')
            .in('id', ids),
          supabase.rpc('fila_de_suplentes', { p_evento: eventoIdNum }),
          supabase.rpc('painel_vagas', { p_evento: eventoIdNum }),
          supabase
            .from('vw_respostas_inscricao')
            .select('inscricao_id, ordem, enunciado, tipo, resposta_texto')
            .in('inscricao_id', ids),
        ]);
        if (resInscritos.error) throw resInscritos.error;

        const inscritos = (resInscritos.data as LinhaInscrito[]) || [];
        if (inscritos.length === 0) {
          if (ativo) setEstado('sem-acesso');
          return;
        }

        // Seções das perguntas (o nome do bloco vem da própria ficha).
        const modalidadeIds = [...new Set(inscritos.map((i) => Number(i.modalidade_id)))];
        const secaoPorEnunciado = new Map<string, string>();
        if (modalidadeIds.length > 0) {
          const { data: forms } = await supabase
            .from('formularios')
            .select('id')
            .in('modalidade_id', modalidadeIds);
          const formIds = ((forms as { id: number }[]) || []).map((f) => Number(f.id));
          if (formIds.length > 0) {
            const { data: perguntas } = await supabase
              .from('perguntas')
              .select('enunciado, secao')
              .in('formulario_id', formIds);
            ((perguntas as { enunciado: string; secao: string | null }[]) || []).forEach((p) => {
              if (p.secao) secaoPorEnunciado.set(normalizar(p.enunciado), p.secao);
            });
          }
        }

        // Fotos da ficha (caminhos no storage).
        const respostas = (resRespostas.data as LinhaResposta[]) || [];
        const caminhos = [
          ...new Set(
            respostas
              .filter((r) => r.tipo === 'foto' && r.resposta_texto)
              .map((r) => r.resposta_texto as string),
          ),
        ];
        const urlPorCaminho = new Map<string, string>();
        if (caminhos.length > 0) {
          const { data: signed } = await supabase.storage
            .from('fichas')
            .createSignedUrls(caminhos, 3600);
          (signed || []).forEach((s) => {
            if (s.signedUrl) urlPorCaminho.set(s.path, s.signedUrl);
          });
        }

        const fila = ((resFila.data as LinhaFila[]) || []).slice();
        const posicaoMap = new Map<number, number>();
        const senhaFilaMap = new Map<number, string | null>();
        fila.forEach((f) => {
          posicaoMap.set(Number(f.inscricao_id), f.posicao);
          senhaFilaMap.set(Number(f.inscricao_id), f.senha);
        });

        const detalheMap = new Map<number, LinhaDetalhe>();
        ((resDetalhe.data as LinhaDetalhe[]) || []).forEach((d) =>
          detalheMap.set(Number(d.id), d),
        );

        const grupos = ((resPainel.data as GrupoVagas[]) || []).filter(
          (g) => g.vagas !== null && g.vagas !== undefined,
        );
        const temVagas = grupos.length > 0;

        const respostasPorInscricao = new Map<number, RespostaImpressao[]>();
        respostas.forEach((r) => {
          const fotoUrl =
            r.tipo === 'foto' && r.resposta_texto
              ? (urlPorCaminho.get(r.resposta_texto) ?? null)
              : null;
          if (!respostasPorInscricao.has(r.inscricao_id)) {
            respostasPorInscricao.set(r.inscricao_id, []);
          }
          respostasPorInscricao.get(r.inscricao_id)!.push({
            secao: secaoPorEnunciado.get(normalizar(r.enunciado)) ?? null,
            enunciado: r.enunciado,
            ordem: Number(r.ordem),
            tipo: r.tipo,
            resposta: r.resposta_texto,
            fotoUrl,
          });
        });

        const lista: PessoaImpressao[] = inscritos.map((i) => {
          const det = detalheMap.get(Number(i.inscricao_id));
          const posicao = posicaoMap.get(Number(i.inscricao_id)) ?? null;
          const senha = det?.senha_fila ?? senhaFilaMap.get(Number(i.inscricao_id)) ?? null;
          let situacao: string;
          if (i.status === 'cancelada') situacao = 'Cancelada';
          else if (i.status === 'lista_espera') {
            situacao = posicao ? `Suplente nº ${posicao}` : 'Suplente';
          } else if (temVagas) situacao = 'Com vaga';
          else situacao = STATUS_LABEL[i.status] ?? '—';

          const respostasPessoa = (respostasPorInscricao.get(Number(i.inscricao_id)) || []).slice();
          const foto = respostasPessoa.find((r) => r.tipo === 'foto' && r.fotoUrl)?.fotoUrl ?? null;

          const mesmaPessoa = normalizar(i.responsavel ?? '') === normalizar(i.participante ?? '');

          return {
            inscricaoId: Number(i.inscricao_id),
            nome: i.participante,
            cpf: i.cpf,
            nascimento: i.nascimento,
            idade: calcularIdade(i.nascimento, evento.inicio_em),
            modalidade: i.modalidade,
            grupo: i.grupo,
            situacao,
            senha,
            telefone: i.participante_telefone,
            email: i.participante_email,
            contatoNome: mesmaPessoa ? 'A própria pessoa' : i.responsavel,
            contatoTelefone: mesmaPessoa ? i.participante_telefone : i.responsavel_telefone,
            fotoUrl: foto,
            respostas: respostasPessoa,
          };
        });

        if (!ativo) return;
        setEventoNome(evento.nome);
        setEventoData(
          [formatarDataCurta(evento.inicio_em), evento.local_nome, evento.cidade]
            .filter(Boolean)
            .join(' · '),
        );
        setPessoas(lista);
        setEstado('pronto');
      } catch (e) {
        if (ativo) {
          setErro(mensagemAmigavel(e));
          setEstado('erro');
        }
      }
    })();
    return () => {
      ativo = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authPronto, user, eventoIdNum, ids.join(',')]);

  const impressoEm = useMemo(() => agoraTexto(), []);

  if (estado === 'carregando') {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background-100">
        <span className="flex h-12 w-12 items-center justify-center text-primary-500">
          <i className="ri-loader-4-line animate-spin text-4xl" />
        </span>
      </div>
    );
  }

  if (estado === 'erro' || estado === 'sem-acesso') {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background-100 px-4 py-8">
        <div className="w-full max-w-[420px] rounded-2xl border border-secondary-200 bg-white p-8 text-center">
          <h1 className="text-xl font-medium text-foreground-950">
            {estado === 'erro' ? 'Não foi possível carregar' : 'Sem acesso'}
          </h1>
          <p className="mt-2 text-sm text-foreground-600">
            {erro ?? 'Você não administra este evento.'}
          </p>
          <button
            type="button"
            onClick={() => navigate(`/gerenciar/${eventoId}`)}
            className="mt-6 w-full cursor-pointer whitespace-nowrap rounded-xl bg-primary-500 px-6 py-3 text-sm font-semibold text-white transition-colors hover:bg-primary-600"
          >
            Voltar ao painel
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background-100">
      <style>{`
        @media print {
          @page { size: A4 portrait; margin: 10mm; }
          body { background: #fff !important; }
          .ficha-pagina { break-after: page; page-break-after: always; border: 0 !important; border-radius: 0 !important; padding: 0 !important; }
          .ficha-pagina:last-child { break-after: auto; page-break-after: auto; }
          .nao-imprimir { display: none !important; }
        }
      `}</style>

      <div className="nao-imprimir mx-auto w-full max-w-[900px] px-4 py-6">
        <button
          type="button"
          onClick={() => navigate(-1)}
          className="inline-flex cursor-pointer items-center gap-1.5 text-sm font-medium text-secondary-600 transition-colors hover:text-foreground-900"
        >
          <span className="flex h-4 w-4 items-center justify-center">
            <i className="ri-arrow-left-line" />
          </span>
          Voltar
        </button>
        <h1 className="mt-2 text-2xl font-bold text-foreground-950">Imprimir ficha</h1>
        <p className="text-sm text-secondary-500">
          {eventoNome}
          {pessoas.length > 1
            ? ` · ${pessoas.length} fichas, uma por página`
            : pessoas[0]
              ? ` · ${pessoas[0].nome}`
              : ''}
        </p>

        <button
          type="button"
          onClick={() => window.print()}
          className="mt-4 inline-flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-xl bg-primary-500 px-5 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-primary-600"
        >
          <span className="flex h-4 w-4 items-center justify-center">
            <i className="ri-printer-line" />
          </span>
          Imprimir
        </button>
      </div>

      <div className="mx-auto w-full max-w-[900px] space-y-6 px-4 pb-10">
        {pessoas.map((p) => (
          <FichaImpressao
            key={p.inscricaoId}
            pessoa={p}
            eventoNome={eventoNome}
            eventoData={eventoData}
            impressoEm={impressoEm}
          />
        ))}
      </div>
    </div>
  );
}